import { DashboardContainerComponent } from '../layout/dashboard-container/dashboard-container.component';
import { FilterListContainerComponent } from '../layout/filter-list-container/filter-list-container.component';
import { ImpactAnalysisHeroSummaryComponent } from '../components/impact-analysis/hero-summary/impact-analysis-hero-summary.component';
import { ImpactAnalysisIncompletionChartComponent } from '../components/impact-analysis/incompletion-chart/impact-analysis-incompletion-chart.component';
import { DynamicCardsComponent } from '../components/dynamic-cards/dynamic-cards.component';
import { ChartComponent } from '../layout/chart/chart.component';
import { Area, AssociateCertSummaryChart, EmptyFilterSchema, ImpactAnalysisCourseTableSchema, ImpactTrainingPredictionTableSchema, ImpactStats, ImpactAnalysisLeadersCourseTableSchema} from './impact-analysis.model';
import { dynamicComponentSchema, InputProperty, SECTION_TYPE, TabConfig, TabGroupConfig } from './form.model';
import { SearchConfig, ApiEnvNames } from './api.model';
import { FETCH_METHODS } from './common.model';
import { CHART_TYPE } from './chart.model';
import { AreaMutipleSelect, TotalCountText } from './form-fields.model';

export type ImpactGroupType = 'Central' | 'Eastern' | 'Southern' | 'Western' | 'leaders';

const LexusHqArea = 'LEXUS HQ';
const LeadersArea = 'leaders';
const ImpactAnalysisExportApiPaths = {
  CourseDetails: 'exportCourseDetailsAreawise',
  AssociateCertSummary: 'exportCertificationpiChartDetails',
} as const;

const AreaApiQueryMap: Record<Area, string> = {
  Central: 'CENTRAL AREA',
  Eastern: 'EASTERN AREA',
  Southern: 'SOUTHERN AREA',
  Western: 'WESTERN AREA',
  'Lexus HQ': LexusHqArea,
};

const LexusCollegeAreaFilterOptions = (['Central', 'Eastern', 'Southern', 'Western', 'Lexus HQ'] as Area[]).map((area) => ({
  fieldLabel: area,
  fieldValue: AreaApiQueryMap[area],
}));

export const ImpactSectionNames = {
  RiskSummary: 'impactRiskSummary',
  CourseDetails: 'impactCourseDetails',
  LeadersSummary: 'impactLeadersSummary',
  TrainingPrediction: 'impactTrainingPrediction',
  AssociateCertSummary: 'impactAssociateCertSummary',
} as const;

export type ImpactFetchMethod = typeof ImpactSectionNames[keyof typeof ImpactSectionNames];

export const ImpactFetchConfig: Record<ImpactFetchMethod, SearchConfig> = {
  [ImpactSectionNames['RiskSummary']]: {
    method: FETCH_METHODS.Get,
    path: 'getRiskSummary',
    urlPath: ApiEnvNames['ImpactAnalysis'],
    body: {
      area: '',
    },
  },
  [ImpactSectionNames['CourseDetails']]: {
    method: FETCH_METHODS.Get,
    path: 'getCourseDetailsAreawise',
    urlPath: ApiEnvNames['ImpactAnalysis'],
    body: {
      area: '',
    },
  },
  [ImpactSectionNames['LeadersSummary']]: {
    method: FETCH_METHODS.Get,
    path: 'getCourseDetailsAreawise',
    urlPath: ApiEnvNames['ImpactAnalysis'],
    body: {
      area: '',
    },
  },
  [ImpactSectionNames['TrainingPrediction']]: {
    method: FETCH_METHODS.Get,
    path: 'getTrainingPrediction',
    urlPath: ApiEnvNames['ImpactAnalysis'],
  },
  [ImpactSectionNames['AssociateCertSummary']]: {
    method: FETCH_METHODS.Get,
    path: 'getCertificationpiChartDetails',
    urlPath: ApiEnvNames['ImpactAnalysis'],
    body: {
      area: '',
    },
  },
};

export const createAreaApiConfig = (area: Area, endpoint: string): SearchConfig => ({
  method: FETCH_METHODS.Get,
  path: endpoint,
  urlPath: ApiEnvNames['ImpactAnalysis'],
  body: {
    area: AreaApiQueryMap[area],
  },
});

export const createLeadersApiConfig = (endpoint: string): SearchConfig => ({
  method: FETCH_METHODS.Get,
  path: endpoint,
  urlPath: ApiEnvNames['ImpactAnalysis'],
  body: {
    area: '',
  },
});

export const createLeadersDownloadApiConfig = (endpoint: string): SearchConfig => ({
  method: FETCH_METHODS.Get,
  path: endpoint,
  urlPath: ApiEnvNames['ImpactAnalysis'],
  body: {},
});

export interface ImpactCardSchema {
  label: string;
  fieldKey: string;
  subLabel: string;
  suffix?: string;
  value?:  number;
  icon?: string;
}

export const ImpactAnalysisCardsSchema: ImpactCardSchema[] = [
  {  label: 'Total Associates Compliant',subLabel:'Live metric', fieldKey: 'total_associates_compliant', icon: 'fa-solid fa-circle-check' },
  {  label: 'Total Associates Pending',subLabel:'Action required', fieldKey: 'total_associates_pending', icon: 'fa-solid fa-triangle-exclamation' },
  {  label: 'Average Completion',subLabel:'Target: 95%', fieldKey: 'average_completion', suffix: '%', icon: 'fa-solid fa-chart-line' },
];

export const ImpactAnalysisNewTabIds = {
  Central: 'central',
  Eastern: 'eastern',
  Southern: 'southern',
  Western: 'western',
  Lexus: 'lexus',
} as const;

const buildAreaGridSchema = (area: Area): dynamicComponentSchema[] => {
  const stats: ImpactStats = {
    totalRemaining: 0,
    totalCompliant: 0,
    avgCompletion: 0,
    urgentCourses: 0,
    highRiskCourses: 0,
    losingMasterThisMonth: 0,
    losingLMLNextMonth: 0,
    riskSummary: { losingLML: 0, high: 0, medium: 0, low: 0 },
  };

  return [
    {
      component: DynamicCardsComponent,
      type: SECTION_TYPE.Dashboard,
      inputs: {
        elementId: 'cards',
        schema: ImpactAnalysisCardsSchema,
        apiConfig: createAreaApiConfig(area, 'getImpactAnalysisSummary'),
        columns: 3,
        rows: 1,
        groupType: area,
        showIcons: true,
      },
    },
    {
      component: ImpactAnalysisHeroSummaryComponent,
      type: SECTION_TYPE.Dashboard,
      inputs: {
        elementId: 'hero',
        stats,
        apiConfig: createAreaApiConfig(area, 'getCertificationAndPaymentSummary'),
        riskApiConfig: createAreaApiConfig(area, 'getRiskSummary'),
        groupType: area,
      },
    },
    {
      component: FilterListContainerComponent,
      type: SECTION_TYPE.List,
      inputs: {
        elementId: 'ImpactCourseSummary',
        DataTableSchema: {
          ...structuredClone(ImpactAnalysisCourseTableSchema),
          downloadApiConfig: createAreaApiConfig(area, ImpactAnalysisExportApiPaths.CourseDetails),
          exportConfig: {
            ...structuredClone(ImpactAnalysisCourseTableSchema.exportConfig),
            exportMessage: `${area} Course Details Data`,
            fileName: `${area.replace(' ', '_')}_Course_Details`,
          },
        },
        forSection: true,
        filterSchema: structuredClone(EmptyFilterSchema),
        onLoadFetch: true,
        apiConfig: createAreaApiConfig(area, 'getCourseDetailsAreawise'),
        globalFilterFields: ['area'],
        groupType: area,
      },
    },
    {
      component: ImpactAnalysisIncompletionChartComponent,
      type: SECTION_TYPE.Dashboard,
      inputs: {
        elementId: 'chart',
        loading: true,
        courseRows: [],
        apiConfig: createAreaApiConfig(area, 'getCourseDetailsAreawise'),
        groupType: area,
      },
    },
    {
      component: ChartComponent,
      type: SECTION_TYPE['Chart'],
      inputs: {
        elementId: ImpactSectionNames['AssociateCertSummary'],
        chartSchema: structuredClone(AssociateCertSummaryChart),
        chartType: CHART_TYPE['PieChart'],
        onLoadFetch: true,
        filterSchema: structuredClone(EmptyFilterSchema),
        exportConfig: {
            fileName: `${area.replace(' ', '_')}_Associate_Certification_Summary`,
            exportMessage: `${area} Associate Certification Summary Data`,
            schema: {
              spinId: 'SPIN ID',
              firstName: 'FIRST NAME',
              lastName: 'LAST NAME',
            },
            groupedSheets: {
              useResponseKeys: true,
              schemaMap: {
                Summary: { certification: 'CERTIFICATION', count: 'COUNT' }
              }
            },
            downloadApiConfig: createAreaApiConfig(area, ImpactAnalysisExportApiPaths.AssociateCertSummary)
        },
        apiConfig: {
          ...ImpactFetchConfig[ImpactSectionNames['AssociateCertSummary']],
          body: { area: AreaApiQueryMap[area] },
        },
        infoFields: structuredClone([{
            ...TotalCountText, calculateBy: 'count'
        }]),
      },
    },
  ];
};

const buildLeadersGridSchema = (): dynamicComponentSchema[] => {
  const stats: ImpactStats = {
    totalRemaining: 0,
    totalCompliant: 0,
    avgCompletion: 0,
    urgentCourses: 0,
    highRiskCourses: 0,
    losingMasterThisMonth: 0,
    losingLMLNextMonth: 0,
    riskSummary: { losingLML: 0, high: 0, medium: 0, low: 0 },
  };

  return [
    {
      component: DynamicCardsComponent,
      type: SECTION_TYPE.Dashboard,
      inputs: {
        elementId: 'cards',
        schema: ImpactAnalysisCardsSchema,
        apiConfig: createLeadersApiConfig('getImpactAnalysisSummary'),
        columns: 3,
        rows: 1,
        groupType: 'leaders' as ImpactGroupType,
        showIcons: true,
      },
    },
    {
      component: ImpactAnalysisHeroSummaryComponent,
      type: SECTION_TYPE.Dashboard,
      inputs: {
        elementId: 'hero',
        stats,
        apiConfig: createLeadersApiConfig('getCertificationAndPaymentSummary'),
        riskApiConfig: createLeadersApiConfig('getRiskSummary'),
        groupType: 'leaders' as ImpactGroupType,
      },
    },
    // {
    //   component: FilterListContainerComponent,
    //   type: SECTION_TYPE.List,
    //   inputs: {
    //     elementId: 'ImpactRiskSummary',
    //     DataTableSchema: {
    //       ...structuredClone(ImpactAnalysisRiskTableSchema),
    //       exportConfig: {
    //         ...structuredClone(ImpactAnalysisRiskTableSchema.exportConfig),
    //         exportMessage: 'Risk Summary Data',
    //         fileName: 'Risk_Summary_Data',
    //       },
    //     },
    //     forSection: true,
    //     filterSchema: structuredClone(EmptyFilterSchema),
    //     onLoadFetch: true,
    //     apiConfig: {
    //       ...ImpactFetchConfig[ImpactSectionNames['RiskSummary']],
    //       body: { area: '' },
    //     },
    //     globalFilterFields: ['group'],
    //     groupType: 'leaders' as ImpactGroupType,
    //   },
    // },
    {
      component: FilterListContainerComponent,
      type: SECTION_TYPE.List,
      inputs: {
        elementId: 'ImpactCourseSummary',
        DataTableSchema: {
          ...structuredClone(ImpactAnalysisLeadersCourseTableSchema),
          downloadApiConfig: {
            ...createLeadersDownloadApiConfig(ImpactAnalysisExportApiPaths.CourseDetails),
            body: { area: '' },
          },
          exportConfig: {
            ...structuredClone(ImpactAnalysisLeadersCourseTableSchema.exportConfig),
            exportMessage: `Course Details Data`,
            fileName: 'Course_Details',
          },
        },
        forSection: true,
        filterSchema: {
          FieldsArray: structuredClone([
            { ...structuredClone(AreaMutipleSelect), displayName: 'Area', options: LexusCollegeAreaFilterOptions , defaultValue: LexusCollegeAreaFilterOptions.map(option => option.fieldValue)},
          ]),
          ButtonsArray: [],
        },
        onLoadFetch: true,
        apiConfig: createLeadersApiConfig('getCourseDetailsAreawise'),
        globalFilterFields: ['group'],
        groupType: 'leaders' as ImpactGroupType,
      },
    },
    // {
    //   component: FilterListContainerComponent,
    //   type: SECTION_TYPE.List,
    //   inputs: {
    //     elementId: 'LeadersSummary',
    //     DataTableSchema: {
    //       ...structuredClone(ImpactLeadersSummaryTableSchema),
    //       isExportRequired: false,
    //       isFilterListExportRequired:true,
    //       isSearchRequired:false,
    //       exportConfig: {
    //         ...structuredClone(ImpactLeadersSummaryTableSchema.exportConfig),
    //       exportMessage: 'Course Details Data',
    //       fileName: 'Course_Details_Data',
    //       },
    //     },
    //     forSection: true,
    //     filterSchema: {
    //       FieldsArray: structuredClone([
    //         {
    //           ...structuredClone(AreaMutipleSelect),
    //           displayName: 'Area',
    //           options: LexusCollegeAreaFilterOptions,
    //           defaultValue: LexusCollegeAreaFilterOptions.map(option => option.fieldValue),
    //         },
    //       ]),
    //       ButtonsArray: [],
    //     },
    //     onLoadFetch: true,
    //     apiConfig: ImpactFetchConfig[ImpactSectionNames['LeadersSummary']],
    //     globalFilterFields: ['group'],
    //     groupType: 'leaders' as ImpactGroupType,
    //   },
    // },
    {
      component: FilterListContainerComponent,
      type: SECTION_TYPE.List,
      inputs: {
        elementId: 'TrainingPrediction',
        DataTableSchema: {
          ...structuredClone(ImpactTrainingPredictionTableSchema),
          exportConfig: {
            ...structuredClone(ImpactTrainingPredictionTableSchema.exportConfig),
            exportMessage: 'Training Prediction Data',
            fileName: 'Training_Prediction_Data',
          },
        },
        forSection: true,
        filterSchema: structuredClone(EmptyFilterSchema),
        onLoadFetch: true,
        apiConfig: ImpactFetchConfig[ImpactSectionNames['TrainingPrediction']],
        globalFilterFields: ['group'],
        groupType: 'leaders' as ImpactGroupType,
      },
    },
     {
      component: ChartComponent,
      type: SECTION_TYPE['Chart'],
      inputs: {
        elementId: ImpactSectionNames['AssociateCertSummary'],
        chartSchema: structuredClone(AssociateCertSummaryChart),
        chartType: CHART_TYPE['PieChart'],
        onLoadFetch: true,
        filterSchema: structuredClone(EmptyFilterSchema),
        exportConfig: {
            fileName: 'Associate_Certification_Summary_Data',
            exportMessage: 'Associate Certification Summary Data',
            schema: {
              spinId: 'Spin ID',
              firstName: 'First Name',
              lastName: 'LAST NAME',
            },
            groupedSheets: {
              useResponseKeys: true,
              schemaMap: {
                Summary: { certification: 'CERTIFICATION', count: 'COUNT' }
              }
            },
            downloadApiConfig: createLeadersDownloadApiConfig(ImpactAnalysisExportApiPaths.AssociateCertSummary)
        },
         apiConfig: ImpactFetchConfig[ImpactSectionNames['AssociateCertSummary']],
        infoFields: structuredClone([{
            ...TotalCountText, calculateBy: 'count'
        }]),
      },
    },
  ];
};

const createAreaTab = (label: string, tabId: string, area: Area | 'Lexus HQ'): TabConfig => {
  const inputs: InputProperty = {
    gridSchema: buildAreaGridSchema(area),
  };

  return {
    label,
    tabId,
    type: SECTION_TYPE.Dashboard,
    component: DashboardContainerComponent,
    inputs,
  };
};

const createLeadersTab = (label: string, tabId: string): TabConfig => {
  const inputs: InputProperty = {
    gridSchema: buildLeadersGridSchema(),
  };

  return {
    label,
    tabId,
    type: SECTION_TYPE.Dashboard,
    component: DashboardContainerComponent,
    inputs,
  };
};

export const ImpactAnalysisNewAreaTabSchema: TabConfig[] = [
  createAreaTab('Central', ImpactAnalysisNewTabIds.Central, 'Central'),
  createAreaTab('Eastern', ImpactAnalysisNewTabIds.Eastern, 'Eastern'),
  createAreaTab('Southern', ImpactAnalysisNewTabIds.Southern, 'Southern'),
  createAreaTab('Western', ImpactAnalysisNewTabIds.Western, 'Western'),
  createAreaTab('Lexus HQ', ImpactAnalysisNewTabIds.Lexus, 'Lexus HQ'),
];

export const ImpactAnalysisNewLeadersTabSchema: TabConfig[] = [
  createLeadersTab('LEXUS HQ', 'leaders'),
];

const cloneTabs = (tabs: TabConfig[]): TabConfig[] =>
  tabs.map((tab) => ({
    ...tab,
    inputs: {
      ...tab.inputs,
      gridSchema: tab.inputs?.gridSchema?.map((grid) => ({
        ...grid,
        inputs: {
          ...grid.inputs,
          ...(grid.inputs?.DataTableSchema ? { DataTableSchema: structuredClone(grid.inputs.DataTableSchema) } : {}),
          ...(grid.inputs?.filterSchema ? { filterSchema: structuredClone(grid.inputs.filterSchema) } : {}),
          ...(grid.inputs?.courseRows ? { courseRows: [...grid.inputs.courseRows] } : {}),
          ...(grid.inputs?.chartRows ? { chartRows: [...grid.inputs.chartRows] } : {}),
        },
      })),
    },
  }));

export interface ImpactAnalysisNewTabsConfig {
  areaTabs: TabConfig[];
  leadersTabs: TabConfig[];
  groups: TabGroupConfig[];
}

export const createImpactAnalysisNewTabsConfig = (): ImpactAnalysisNewTabsConfig => {
  const areaTabs = cloneTabs(ImpactAnalysisNewAreaTabSchema);
  const leadersTabs = cloneTabs(ImpactAnalysisNewLeadersTabSchema);

  return {
    areaTabs,
    leadersTabs,
    groups: [
      {
        label: 'Area PCs',
        tabs: areaTabs,
      },
      {
        label: 'Lexus College',
        tabs: leadersTabs,
      },
    ],
  };
};
